/**
 * SEO route manifest — the ONE source of truth for every public page's
 * <title>, meta description, canonical URL and sitemap entry.
 *
 * Read by:
 *   - src/app.ts            — server injects the page's <head> tags into index.html
 *                             (crawlers get per-page tags without running JS) and
 *                             returns a real 404 for any path not listed here.
 *   - frontend RouteSeo     — re-renders the same tags on client-side navigation.
 *   - scripts/build-sitemap — sitemap.xml = every indexable entry here + blog posts.
 *
 * Why (audit 2026-10-01 C1): every route served the same index.html with
 * `<link rel=canonical href="https://printpartyhub.com">` and the home-page
 * title, so Google consolidated all 36 URLs into the home page.
 *
 * Pure data — no Node or DOM imports — so both the Bun server and Vite can load it.
 */
import { POSTS, SITE_URL, DEFAULT_OG_IMAGE } from "./blog-posts";

export { SITE_URL, DEFAULT_OG_IMAGE };

/** Bump when static page content changes materially (sitemap <lastmod>). */
export const SITE_LASTMOD = "2026-10-01";

export interface SeoRoute {
  path: string;
  title: string;
  description: string;
  /** Excluded from the sitemap and served with robots noindex. */
  noindex?: boolean;
  priority?: number;
  changefreq?: "weekly" | "monthly" | "yearly";
  lastmod?: string;
}

const STATIC_ROUTES: SeoRoute[] = [
  { path: "/", title: "PrintPartyHub — Custom Printable Party Games, Bingo & Puzzles", description: "Make custom bingo cards, word searches, crosswords, scavenger hunts, trivia and party game bundles. Preview free, then download a print-ready PDF. No account needed.", priority: 1.0, changefreq: "weekly" },
  { path: "/bingo", title: "Custom Bingo Card Generator — Printable PDF | PrintPartyHub", description: "Create up to 100 uniquely shuffled bingo cards with your own words, title and logo. 3×3 to 5×5 grids, 1–4 cards per page. Print-ready PDF.", priority: 0.9, changefreq: "monthly" },
  { path: "/word-search", title: "Custom Word Search Maker — Printable Puzzles | PrintPartyHub", description: "Turn any word list into a printable word search with an answer key. 10×10 to 20×20 grids — great for classrooms, showers and parties.", priority: 0.9, changefreq: "monthly" },
  { path: "/crosswords", title: "Custom Crossword Puzzle Maker — Printable PDF with Clues | PrintPartyHub", description: "Build a printable crossword from your own words and clues, or pick a themed pack. Answer key included.", priority: 0.9, changefreq: "monthly" },
  { path: "/scavenger-hunt", title: "Scavenger Hunt Generator — Printable Hunt Cards | PrintPartyHub", description: "Printable scavenger hunt cards for kids' birthdays, backyards, offices and classrooms. Themed lists or your own items, uniquely shuffled per player.", priority: 0.9, changefreq: "monthly" },
  { path: "/trivia-pdf", title: "Printable Trivia Game Maker — Questions, Answer Key & Team Sheets | PrintPartyHub", description: "Generate a printable trivia night pack: up to 100 questions across categories, multiple choice or open answer, with answer key and team score sheets.", priority: 0.85, changefreq: "monthly" },
  { path: "/custom", title: "Custom Party Game Builder — Your Own Content | PrintPartyHub", description: "Build your own printable bingo, word search, trivia, mad libs or scavenger hunt from your own content. Print-ready PDF.", priority: 0.8, changefreq: "monthly" },
  { path: "/baby-shower", title: "Printable Baby Shower Games Bundle — 8 Games | PrintPartyHub", description: "Eight printable baby shower games personalized with the mom-to-be's name: bingo, word scramble, predictions, baby tunes, mad libs and more.", priority: 0.85, changefreq: "monthly" },
  { path: "/bridal-shower", title: "Printable Bridal Shower Games Bundle — 8 Games | PrintPartyHub", description: "Eight printable bridal shower games personalized with the couple's names: bridal bingo, he said she said, how well do you know the bride and more.", priority: 0.85, changefreq: "monthly" },
  { path: "/madlibs", title: "Printable Mad Libs Stories — Themed Bundles or Your Own | PrintPartyHub", description: "Printable fill-in-the-blank story bundles for weddings, offices, kids and holidays — or write your own story and print copies for every guest.", priority: 0.85, changefreq: "monthly" },
  { path: "/icebreakers", title: "Corporate Icebreaker Games Kit — Printable | PrintPartyHub", description: "A printable team-building kit: two truths and a lie, team trivia, meeting bingo, would you rather, conversation starters and team superlatives.", priority: 0.85, changefreq: "monthly" },
  { path: "/would-you-rather", title: "Printable Would You Rather Cards | PrintPartyHub", description: "Cut-apart Would You Rather question cards for parties, families, classrooms and offices. Pick categories, theme and cards per page.", priority: 0.8, changefreq: "monthly" },
  { path: "/conversation-starters", title: "Printable Conversation Starter Cards | PrintPartyHub", description: "Printable conversation starter cards for dinners, dates, teams and classrooms — questions that spark real conversations.", priority: 0.8, changefreq: "monthly" },
  { path: "/truth-or-dare", title: "Printable Truth or Dare Cards | PrintPartyHub", description: "Printable truth or dare card decks for sleepovers, parties and game nights, with categories for every audience.", priority: 0.8, changefreq: "monthly" },
  { path: "/never-have-i-ever", title: "Printable Never Have I Ever Cards | PrintPartyHub", description: "Printable Never Have I Ever card decks — clean, party and spicy categories, cut-apart format.", priority: 0.8, changefreq: "monthly" },
  { path: "/prompt-games", title: "Printable Prompt Game Cards — Never Have I Ever & Would You Rather | PrintPartyHub", description: "Printable prompt decks tuned to your audience — kids, family, office or adults — with your own title on every card.", priority: 0.75, changefreq: "monthly" },
  { path: "/couples", title: "Printable Couples & Date Night Games | PrintPartyHub", description: "Printable couples games: this-or-that, couples trivia, date dares and conversation cards personalized with both names.", priority: 0.75, changefreq: "monthly" },
  { path: "/memory-game", title: "Printable Memory Match Card Maker | PrintPartyHub", description: "Make printable memory matching cards from your own word or picture pairs — vocabulary, baby animals, anything. Cut-apart PDF.", priority: 0.75, changefreq: "monthly" },
  { path: "/holiday-games", title: "Printable Holiday Party Game Packs — Christmas, Halloween, Thanksgiving | PrintPartyHub", description: "Printable holiday game packs with bingo cards, trivia, word searches and more for Christmas, Halloween, Thanksgiving, 4th of July and New Year's Eve.", priority: 0.8, changefreq: "monthly" },
  { path: "/drinking-games", title: "Printable Drinking Game Cards (21+) | PrintPartyHub", description: "Printable adult drinking game card sets — Kings Cup, Power Hour and more. For guests 21 and over.", priority: 0.6, changefreq: "monthly" },
  { path: "/emoji-pictionary", title: "Printable Emoji Pictionary Game — Movies, Songs & More | PrintPartyHub", description: "Printable emoji pictionary puzzle sheets with a separate answer key — movies, songs, food and more. Great for showers, classrooms and parties.", priority: 0.8, changefreq: "monthly" },
  { path: "/blog", title: "Party Game Ideas & Printable Activities Blog | PrintPartyHub", description: "Ideas and how-tos for printable party games: bingo, scavenger hunts, shower games, office parties and classroom activities.", priority: 0.7, changefreq: "weekly" },
  { path: "/docs", title: "Help & Documentation — Products, Pricing, Payments | PrintPartyHub", description: "How PrintPartyHub works: products and prices, how payment and downloads work, refunds, the mobile app and how to contact support.", priority: 0.5, changefreq: "monthly" },
  { path: "/about", title: "About PrintPartyHub", description: "PrintPartyHub makes custom printable party games — preview free, pay once, print at home. No accounts, no subscriptions.", priority: 0.5, changefreq: "yearly" },
  { path: "/contact", title: "Contact Us | PrintPartyHub", description: "Questions about a game or a download? Email support@printpartyhub.com.", priority: 0.4, changefreq: "yearly" },
  { path: "/terms", title: "Terms of Service | PrintPartyHub", description: "PrintPartyHub terms of service.", priority: 0.3, changefreq: "yearly" },
  { path: "/privacy", title: "Privacy Policy | PrintPartyHub", description: "PrintPartyHub privacy policy — what we collect (very little) and why.", priority: 0.3, changefreq: "yearly" },
  // Transactional pages: real routes, but never indexed.
  { path: "/checkout", title: "Your Cart | PrintPartyHub", description: "Review your cart and check out.", noindex: true },
  { path: "/pay", title: "Secure Checkout | PrintPartyHub", description: "Pay securely with Stripe.", noindex: true },
  { path: "/download/cart", title: "Download Your Games | PrintPartyHub", description: "Download your purchased games.", noindex: true },
];

const BLOG_ROUTES: SeoRoute[] = POSTS.map((p) => ({
  path: `/blog/${p.slug}`,
  title: `${p.title} | PrintPartyHub`,
  description: p.description,
  priority: 0.7,
  changefreq: "monthly" as const,
  lastmod: p.updatedDate ?? p.publishedDate,
}));

export const SEO_ROUTES: SeoRoute[] = [...STATIC_ROUTES, ...BLOG_ROUTES];

const BY_PATH = new Map(SEO_ROUTES.map((r) => [r.path, r]));

/** Dynamic, per-session pages: valid SPA routes, never indexed. */
const NOINDEX_PATTERNS: Array<{ re: RegExp; title: string }> = [
  { re: /^\/preview\/[A-Za-z0-9_-]+$/, title: "Preview Your Game | PrintPartyHub" },
  { re: /^\/download\/[A-Za-z0-9_-]+$/, title: "Download Your Game | PrintPartyHub" },
  { re: /^\/madlibs\/play\/[A-Za-z0-9_-]+\/\d+$/, title: "Play Mad Libs | PrintPartyHub" },
];

/** Old or removed URLs → where they live now (server answers 301). */
export const REDIRECTS: Record<string, string> = {
  "/faq": "/#faq",
  "/mad-libs": "/madlibs",
  "/party-games": "/#catalog", // Party Game Bundle removed (Jake: "do not re-add")
  "/trivia": "/trivia-pdf", // trivia-night deleted 2026-09-15
  "/trivia-night": "/trivia-pdf",
};

export type SeoResolution =
  | { kind: "page"; route: SeoRoute }
  | { kind: "redirect"; location: string }
  | { kind: "not-found" };

export function resolveSeo(pathname: string): SeoResolution {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    // Collapse leading slashes too: "//evil.com/" must never become a
    // protocol-relative Location (open redirect).
    const loc = "/" + pathname.replace(/^[\/\\]+/, "").replace(/\/+$/, "");
    return { kind: "redirect", location: /^\/[\/\\]/.test(loc) ? "/" : loc };
  }
  const hit = BY_PATH.get(pathname);
  if (hit) return { kind: "page", route: hit };
  if (REDIRECTS[pathname]) return { kind: "redirect", location: REDIRECTS[pathname] };
  for (const p of NOINDEX_PATTERNS) {
    if (p.re.test(pathname)) {
      return { kind: "page", route: { path: pathname, title: p.title, description: "PrintPartyHub", noindex: true } };
    }
  }
  return { kind: "not-found" };
}

export const NOT_FOUND_ROUTE: SeoRoute = {
  path: "/404",
  title: "Page not found | PrintPartyHub",
  description: "That page doesn't exist. Browse PrintPartyHub's printable party games.",
  noindex: true,
};

export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * The <head> tags for a route, tagged `data-rh="true"` so react-helmet-async
 * adopts and replaces them on client-side navigation instead of duplicating them.
 */
export function renderHeadTags(route: SeoRoute): string {
  const title = esc(route.title);
  const desc = esc(route.description);
  const tags = [
    `<title>${title}</title>`,
    `<meta data-rh="true" name="description" content="${desc}" />`,
    `<meta data-rh="true" name="robots" content="${route.noindex ? "noindex, nofollow" : "index, follow"}" />`,
    `<meta data-rh="true" property="og:title" content="${title}" />`,
    `<meta data-rh="true" property="og:description" content="${desc}" />`,
    `<meta data-rh="true" name="twitter:title" content="${title}" />`,
    `<meta data-rh="true" name="twitter:description" content="${desc}" />`,
  ];
  if (!route.noindex) {
    const url = esc(canonicalUrl(route.path));
    tags.push(`<link data-rh="true" rel="canonical" href="${url}" />`);
    tags.push(`<meta data-rh="true" property="og:url" content="${url}" />`);
  }
  return tags.join("\n    ");
}

export const SEO_START = "<!--seo:start-->";
export const SEO_END = "<!--seo:end-->";

/** Replace the marked region of index.html with the route's head tags. */
export function injectHead(indexHtml: string, route: SeoRoute): string {
  const a = indexHtml.indexOf(SEO_START);
  const b = indexHtml.indexOf(SEO_END);
  if (a < 0 || b < a) return indexHtml;
  return indexHtml.slice(0, a + SEO_START.length) + "\n    " + renderHeadTags(route) + "\n    " + indexHtml.slice(b);
}

/** Indexable routes, in sitemap order. */
export function sitemapRoutes(): SeoRoute[] {
  return SEO_ROUTES.filter((r) => !r.noindex);
}

export function buildSitemapXml(): string {
  const rows = sitemapRoutes().map((r) => {
    const lastmod = r.lastmod ?? SITE_LASTMOD;
    return `  <url><loc>${canonicalUrl(r.path)}</loc><lastmod>${lastmod}</lastmod><changefreq>${r.changefreq ?? "monthly"}</changefreq><priority>${(r.priority ?? 0.5).toFixed(2)}</priority></url>`;
  });
  return `<?xml version="1.0" encoding="UTF-8"?>\n<!-- Generated by scripts/build-sitemap.ts from src/seo/routes.ts — do not hand-edit. -->\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${rows.join("\n")}\n</urlset>\n`;
}
